import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { bind, next, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { Theme } from 'webapp/enums/enums';
import { equal, uniq } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import BoardsTabsNavItemComponent from 'webapp/components/boards/tabs-nav-item';

import type Board from 'webapp/models-custom/board';

interface Args {
	model: Board[];
	selectedId: string;
	onOpen: (id: string) => void;
}

export default class BoardsTabsNavComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	@tracked boardsForNavigation: Board[] = [];
	@tracked boardsForMoreList: Board[] = [];

	private _containerEl: JQuery<HTMLElement> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.boardsForNavigation = this.args.model;
	}

	@action
	scheduleUpdateItems() {
		this.boardsForNavigation = this.args.model;
		next(() => {
			scheduleOnce('afterRender', this, this.updateItems);
		});
	}

	@action
	updateItems() {
		$('#topbar .nav-container li').each((_k, node) => {
			bind(this, this._getWidthForItem, $(node));
		});
		this.updateItemVisibility();
	}

	@action
	updateItemVisibility() {
		const minItemWidth = 80;

		const topbarCt = $('#topbar'),
			spaceForBoards = topbarCt.width()! - 24, // leave space for dots
			boardsForMoreIds: string[] = [];

		let boardsForNavigationIds: string[] = [];

		if (!this._containerEl) {
			this._containerEl = $('.nav-container', topbarCt);
		}

		// first, reserve space for the selected item
		// then, go through all model items and check if they will be visible

		const items = this._containerEl.find('li'),
			selectedWidth = this._getWidthForItem(items.filter('.selected'));

		let spaceLeft = spaceForBoards - selectedWidth;

		this.args.model.forEach((board) => {
			let isVisible = false;

			if (String(this.args.selectedId) === board.id) {
				isVisible = true; // size has already been calculated ^
			} else if (spaceLeft >= minItemWidth) {
				const item = items.filter(function () {
					return String($(this).data('id')) === board.id;
				});

				if (item.length) {
					const itemWidth = this._getWidthForItem(item);
					if (itemWidth && itemWidth <= spaceLeft) {
						isVisible = true;
						spaceLeft -= itemWidth;
					}
				}
			}

			if (isVisible) {
				boardsForNavigationIds.push(board.id!);
			} else {
				boardsForMoreIds.push(board.id!);
			}
		});

		$('ul', topbarCt).css({ maxWidth: spaceForBoards - spaceLeft });

		boardsForNavigationIds.push(String(this.args.selectedId));
		boardsForNavigationIds.push(...boardsForMoreIds);
		boardsForNavigationIds = uniq(boardsForNavigationIds);

		if (
			!equal(
				boardsForNavigationIds,
				this.boardsForNavigation.map((item) => item.id),
			)
		) {
			const boardsForNavigation: Board[] = [];
			boardsForNavigationIds.forEach((boardId) => {
				const board = this.args.model.find((item) => item.id === boardId);

				if (board) {
					boardsForNavigation.push(board);
				}
			});
			this.boardsForNavigation = boardsForNavigation;
		}

		if (
			!equal(
				boardsForMoreIds,
				this.boardsForMoreList.map((item) => item.id),
			)
		) {
			const boardsForMoreList: Board[] = [];
			boardsForMoreIds.forEach((boardId) => {
				const board = this.args.model.find((item) => item.id === boardId);

				if (board) {
					boardsForMoreList.push(board);
				}
			});
			this.boardsForMoreList = boardsForMoreList;
		}
	}

	@action
	openBoard(boardId: string) {
		this.args.onOpen(boardId);
	}

	@action
	openMore() {
		const attachTo = $('#topbar div.more').get(0);
		const popup = this.popupService.createListPopup(attachTo, Theme.DARK, 'filters boardsmore');

		if (popup) {
			const itemsList = this.boardsForMoreList.map((board) => {
				return {
					id: board.id,
					text: board.name,
					action: 'openBoard',
					class: '',
				};
			});

			popup.open({
				itemsList,
				doAction: (_actionName: string, id: string) => {
					this.openBoard(id);
				},
			});
		}
	}

	_getWidthForItem(el: JQuery<HTMLElement>) {
		let width = +el.data('width');
		if (!width) {
			width = Math.ceil(el.outerWidth(true)!);
			el.data('width', width);
		}
		return width;
	}
	<template>
		<div
			class="nav-container"
			{{didInsert this.updateItems}}
			{{didUpdate this.scheduleUpdateItems @model}}
			{{didUpdate this.updateItemVisibility @selectedId}}
			{{onWindowEvents this.updateItemVisibility "resize" 150}}
		>
			<ul id="nav">
				{{#each this.boardsForNavigation as |board|}}
					<BoardsTabsNavItemComponent
						@model={{board}}
						@selectedId={{@selectedId}}
						@onOpen={{this.openBoard}}
					/>
				{{/each}}
			</ul>

			{{#if this.boardsForMoreList}}
				<div role="button" class="more" {{on "click" this.openMore}}>
					<a>...</a>
				</div>
			{{/if}}
		</div>
	</template>
}
